import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, FolderArchive, Paperclip, FileText, ImageIcon, X, Upload, Loader2, AlertCircle, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = { kontrakt: "Kontrakt", forsikring: "Forsikring", aftale: "Aftale", andet: "Andet" };
const CAT_CLS = {
  kontrakt: "bg-sky-100 text-sky-700", forsikring: "bg-violet-100 text-violet-700",
  aftale: "bg-emerald-100 text-emerald-700", andet: "bg-slate-100 text-slate-600",
};
const fileUrl = (path) => `${API_BASE}/documents/file/${path}?auth=${encodeURIComponent(localStorage.getItem("cf_token") || "")}`;
const isPdf = (name = "") => name.toLowerCase().endsWith(".pdf");
const fmtDate = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return `${parseInt(d, 10)}/${parseInt(m, 10)}-${y}`; };
const empty = () => ({ title: "", category: "kontrakt", expiry_date: "", note: "", file_path: "", filename: "", content_type: "" });

export function DocumentsArchive() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch { toast.error("Kunne ikke hente dokumenter"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty()); setLocalPreview(""); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d); setLocalPreview("");
    setForm({ title: d.title, category: d.category, expiry_date: d.expiry_date || "", note: d.note || "", file_path: d.file_path || "", filename: d.filename || "", content_type: d.content_type || "" });
    setDialogOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const okExt = /\.(jpe?g|png|webp|gif|heic|pdf)$/i.test(file.name);
    if (!okExt) { toast.error("Kun billeder eller PDF er tilladt"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Filen er for stor (max 15 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_path: r.data.file_path, filename: r.data.filename, content_type: r.data.content_type || "" }));
      setLocalPreview(URL.createObjectURL(file));
      toast.success("Fil vedhæftet");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede"); }
    finally { setUploading(false); }
  };
  const removeAttachment = () => { setLocalPreview(""); setForm((f) => ({ ...f, file_path: "", filename: "", content_type: "" })); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Skriv en titel"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/documents/${editing.id}`, form);
      else await api.post("/documents", form);
      setDialogOpen(false); toast.success(editing ? "Dokument opdateret" : "Dokument gemt"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke gemme"); }
    finally { setSaving(false); }
  };
  const remove = async (d) => {
    if (!window.confirm("Slet dette dokument?")) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Dokument slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const expiringSoon = docs.filter((d) => d.days_to_expiry != null && d.days_to_expiry >= 0 && d.days_to_expiry <= 30).length;
  const expired = docs.filter((d) => d.expired).length;
  const filtered = docs.filter((d) => catFilter === "all" || d.category === catFilter)
    .sort((a, b) => (a.expiry_date || "9999").localeCompare(b.expiry_date || "9999"));

  const expiryBadge = (d) => {
    if (!d.expiry_date) return null;
    if (d.expired) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600"><AlertCircle className="w-3 h-3" /> Udløbet {fmtDate(d.expiry_date)}</span>;
    if (d.days_to_expiry <= 30) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600"><CalendarClock className="w-3 h-3" /> Udløber om {d.days_to_expiry} dage</span>;
    return <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><CalendarClock className="w-3 h-3" /> Udløber {fmtDate(d.expiry_date)}</span>;
  };

  return (
    <div className="max-w-4xl space-y-5" data-testid="documents-archive">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-light text-slate-900 flex items-center gap-2"><FolderArchive className="w-6 h-6 text-[#38BDF8]" /> Dokumentarkiv</h2>
          <p className="text-sm text-slate-500">Kontrakter, forsikringer og aftaler ét sted — med udløbsdato og påmindelse.</p>
        </div>
        <Button onClick={openNew} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2 self-start sm:self-auto" data-testid="doc-new-btn"><Plus className="w-4 h-4" /> Nyt dokument</Button>
      </div>

      {(expiringSoon > 0 || expired > 0) && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-3 flex items-center gap-2.5" data-testid="doc-expiry-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">{expired > 0 && <><span className="font-semibold">{expired}</span> udløbet. </>}{expiringSoon > 0 && <><span className="font-semibold">{expiringSoon}</span> udløber inden for 30 dage.</>}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Alle"], ...Object.entries(CATS)].map(([id, label]) => (
          <button key={id} onClick={() => setCatFilter(id)} data-testid={`doc-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${catFilter === id ? "bg-[#38BDF8] text-[#0b1120]" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>{label}</button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <p className="text-sm text-slate-500 py-6" data-testid="documents-loading">Henter dokumenter…</p>}
        {!loading && filtered.map((d) => (
          <div key={d.id} className={`rounded-2xl bg-[#ffffff] border p-4 flex flex-col sm:flex-row gap-4 ${d.expired ? "border-red-300" : d.days_to_expiry != null && d.days_to_expiry <= 30 ? "border-amber-300" : "border-[#e2e8f0]"}`} data-testid={`document-${d.id}`}>
            {d.file_path && (
              <button onClick={() => setPreview({ path: d.file_path, filename: d.filename })} className="shrink-0 w-full sm:w-20 h-20 rounded-lg overflow-hidden bg-[#f8fafc] border border-[#e2e8f0] flex items-center justify-center hover:border-[#38BDF8]" data-testid={`doc-thumb-${d.id}`}>
                {isPdf(d.filename) ? <FileText className="w-7 h-7 text-slate-500" /> : <img src={fileUrl(d.file_path)} alt={d.filename} className="w-full h-full object-cover" />}
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${CAT_CLS[d.category] || CAT_CLS.andet}`}>{CATS[d.category] || d.category}</span>
                {expiryBadge(d)}
              </div>
              <p className="font-medium text-slate-800 mt-1.5 break-words">{d.title}</p>
              {d.note && <p className="text-sm text-slate-500 mt-0.5 whitespace-pre-wrap break-words">{d.note}</p>}
              {d.filename && <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {d.filename}</p>}
            </div>
            <div className="flex sm:flex-col gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => openEdit(d)} className="h-8 w-8 text-slate-500 hover:text-sky-600" data-testid={`doc-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(d)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-12 text-center text-slate-500" data-testid="documents-empty">Ingen dokumenter endnu. Klik "Nyt dokument".</div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-lg" data-testid="doc-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light">{editing ? "Redigér dokument" : "Nyt dokument"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Titel</label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="fx 'Lejekontrakt Falster'" className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="doc-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Kategori</label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    {Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Udløbsdato (valgfri)</label>
                <Input type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="doc-expiry-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Note</label>
              <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={3} placeholder="Detaljer, kontaktperson, opsigelsesvarsel…" className="w-full rounded-md bg-[#f8fafc] border border-[#e2e8f0] text-slate-700 text-sm p-2.5 resize-y focus:outline-none focus:ring-1 focus:ring-[#38BDF8]" data-testid="doc-note-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Fil (PDF/billede)</label>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.heic" onChange={handleFile} className="hidden" data-testid="doc-file-input" />
              {form.file_path ? (
                <div className="flex items-center gap-3 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-2.5">
                  {isPdf(form.filename) ? <FileText className="w-9 h-9 text-slate-500 shrink-0" /> : <img src={localPreview || fileUrl(form.file_path)} alt="preview" className="w-12 h-12 rounded object-cover shrink-0" />}
                  <span className="flex-1 text-sm text-slate-500 truncate">{form.filename}</span>
                  <Button size="icon" variant="ghost" onClick={removeAttachment} className="h-8 w-8 text-slate-500 hover:text-red-600 shrink-0" data-testid="doc-remove-file-btn"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full rounded-lg bg-[#f8fafc] border-[#e2e8f0] border-dashed text-slate-500 hover:bg-[#eef2f6] hover:text-slate-900 gap-2 h-16" data-testid="doc-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploader…</> : <><Upload className="w-4 h-4" /> Vedhæft PDF eller billede</>}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="doc-cancel-btn">Annullér</Button>
            <Button onClick={save} disabled={saving || uploading} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2" data-testid="doc-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Gem" : "Gem dokument"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-3xl" data-testid="doc-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light flex items-center gap-2 text-base">{preview && isPdf(preview.filename) ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}{preview?.filename}</DialogTitle></DialogHeader>
          {preview && (isPdf(preview.filename) ? <iframe src={fileUrl(preview.path)} title={preview.filename} className="w-full h-[70vh] rounded-lg bg-white" /> : <img src={fileUrl(preview.path)} alt={preview.filename} className="w-full max-h-[70vh] object-contain rounded-lg" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DocumentsArchive;
